import type paper from "paper";
import {
  projectConstructionLines,
  type ConstructionLine,
  type Project,
  resolveOffsetRoot,
} from "./Project";
import { rotatePrimitive, translatePrimitive } from "../tools/SelectTool";
import { normalizeRotation } from "./constructionRotation";
import { resolveAxisSpecFrom, selfAxisGap } from "./mirrorAxis";

/** Issue #180: derived "mirror" lines. A mirror line carries a
 *  `mirrorSource: { sourceId, axis }` back-ref; the commit pipeline
 *  re-computes its `pathData` (and primitive metadata, when the
 *  source has one) on every commit so it tracks live edits of the
 *  source AND of the axis line.
 *
 *  Two axis flavours, addressed via the construction line's
 *  `(pathIndex, segmentIndex)`:
 *   - `segment` — reflect across the infinite line through that
 *     segment's two endpoints.
 *   - `point`   — central inversion through the anchor (mathematically
 *     a 180° rotation about that point). */

export interface AxisSpec {
  kind: "segment" | "point";
  /** Centre of the reflection. For `segment` it's a point on the
   *  axis line (segment start); for `point` it's the pivot of the
   *  central inversion. */
  origin: { x: number; y: number };
  /** Axis direction (unit vector). Only meaningful for `segment` —
   *  ignored for `point`. */
  dir?: { x: number; y: number };
}

/** Two-by-three affine matrix encoding a reflection. Applied as
 *  `(x', y') = (m11*x + m12*y + tx, m21*x + m22*y + ty)`. Used in
 *  both the commit-time mirror derivation and the hover preview. */
export interface MirrorMatrix {
  m11: number;
  m12: number;
  tx: number;
  m21: number;
  m22: number;
  ty: number;
}

/** Build the mirror matrix for a given axis spec. */
export function buildMirrorMatrix(axis: AxisSpec): MirrorMatrix {
  if (axis.kind === "point") {
    // Central inversion through (ox, oy): (x, y) → (2ox - x, 2oy - y).
    return {
      m11: -1,
      m12: 0,
      tx: 2 * axis.origin.x,
      m21: 0,
      m22: -1,
      ty: 2 * axis.origin.y,
    };
  }
  const dir = axis.dir!;
  const len = Math.hypot(dir.x, dir.y);
  if (!Number.isFinite(len) || len < 1e-9) {
    // Degenerate axis (zero-length segment) — fall back to point
    // inversion so the user still gets *some* reflection rather than
    // a silent no-op.
    return buildMirrorMatrix({ ...axis, kind: "point" });
  }
  const ux = dir.x / len;
  const uy = dir.y / len;
  // Householder reflection across the line through origin with
  // direction (ux, uy):
  //   R = 2 u u^T - I
  // composed with translate-to-origin / translate-back so the line
  // can pass through any point.
  const a = 2 * ux * ux - 1;
  const b = 2 * ux * uy;
  const c = 2 * uy * uy - 1;
  const ox = axis.origin.x;
  const oy = axis.origin.y;
  // After translate-to-origin, apply R, then translate back:
  //   (x', y') = R * (x - o) + o
  return {
    m11: a,
    m12: b,
    tx: ox - a * ox - b * oy,
    m21: b,
    m22: c,
    ty: oy - b * ox - c * oy,
  };
}

/** Apply the matrix to a 2D point — used by the preview overlays
 *  and by `rotatePrimitive` integration below. */
export function applyMirror(
  m: MirrorMatrix,
  p: { x: number; y: number },
): { x: number; y: number } {
  return {
    x: m.m11 * p.x + m.m12 * p.y + m.tx,
    y: m.m21 * p.x + m.m22 * p.y + m.ty,
  };
}

/** Resolve a `MirrorSource['axis']` ref into the absolute axis spec
 *  by reading the current axis line's geometry. Returns `null` when
 *  the axis line / sub-path / anchor is missing, signalling that the
 *  derived mirror should be pruned — which is also what the shared
 *  resolver returns for a segment-axis that has turned curved (#371).
 *  The geometry itself lives in `mirrorAxis.ts`, shared with the
 *  leather Mirror sub-tool (#973); this wrapper only looks the axis
 *  line up by id. */
export function resolveAxisSpec(
  project: Project,
  axisRef: NonNullable<ConstructionLine["mirrorSource"]>["axis"],
  scope: paper.PaperScope,
): AxisSpec | null {
  const axisLine = projectConstructionLines(project).find(
    (l) => l.id === axisRef.constructionId,
  );
  if (!axisLine) return null;
  return resolveAxisSpecFrom(axisLine.pathData, axisRef, scope);
}

/** Mirror a complete pathData string. Reflection inverts winding,
 *  but since construction lines are visual-only, no fix-up is
 *  needed — the resulting `pathData` is round-tripped through
 *  paper.js so curve handles come out in the right places. */
export function mirrorPathData(
  pathData: string,
  m: MirrorMatrix,
  scope: paper.PaperScope,
): string | null {
  if (!pathData) return null;
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  // paper.Matrix takes `(a, b, c, d, tx, ty)` where the transform is:
  //   x' = a*x + c*y + tx
  //   y' = b*x + d*y + ty
  // Our MirrorMatrix names them m11/m12 (row 1) and m21/m22 (row 2),
  // so paper's (a, b, c, d) == (m11, m21, m12, m22).
  const mat = new scope.Matrix(m.m11, m.m21, m.m12, m.m22, m.tx, m.ty);
  cp.transform(mat);
  // Emit pathData with ABSOLUTE coords (`M` / `C` / `Z`) instead of
  // paper's default RELATIVE form (`M` / `c` / `z`). The relative
  // form accumulates floating-point error across the chain of
  // deltas, so on a CLOSED path the final `c`'s end can land a
  // sub-millimetre off the `M` position. When paper parses that
  // pathData back, the "close-back" doesn't byte-match `M` and an
  // extra trailing segment is spawned — breaking the 1:1
  // segmentIndex assumption `smoothAnchors` propagation relies on
  // (the extra segment has no lock annotation, so its handles
  // render in the "independent" style while the mirror's
  // round-tripped seg-0 handles render "locked", visible to the
  // user as one locked + one unlocked handle stack at the polygon's
  // close point). Absolute coords keep `seg0.point` byte-exact
  // through serialise → parse, so the round-trip preserves segment
  // count.
  const out = serializeAbsolutePathData(cp);
  cp.remove();
  return out;
}

/** Serialize a CompoundPath to pathData using ABSOLUTE coords
 *  (`M x,y C x1,y1 x2,y2 x,y ... Z`). The closing edge of a closed
 *  subpath is emitted with the FIRST segment's coords spelled out
 *  explicitly, so the path round-trips through paper's parser with
 *  byte-exact segment count regardless of how the transform
 *  matrix's floating-point math distributed across the deltas. */
function serializeAbsolutePathData(cp: paper.CompoundPath): string {
  const subs: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  let out = "";
  for (const sub of subs) {
    const segs = sub.segments;
    if (!segs || segs.length === 0) continue;
    const seg0 = segs[0]!;
    out += `M${seg0.point.x},${seg0.point.y}`;
    // Emit a C command for each curve segment. For a closed path
    // we additionally emit the closing C (last segment → seg 0)
    // BEFORE the Z so the close-back position is byte-exact.
    const last = sub.closed ? segs.length : segs.length - 1;
    for (let i = 0; i < last; i++) {
      const prev = segs[i]!;
      const next = i + 1 < segs.length ? segs[i + 1]! : seg0;
      const c1x = prev.point.x + prev.handleOut.x;
      const c1y = prev.point.y + prev.handleOut.y;
      const c2x = next.point.x + next.handleIn.x;
      const c2y = next.point.y + next.handleIn.y;
      out += `C${c1x},${c1y} ${c2x},${c2y} ${next.point.x},${next.point.y}`;
    }
    if (sub.closed) out += "Z";
  }
  return out;
}

/** The rotation a mirror carries when its source carries `sourceRot`
 *  (#1192). It puts the mirror's rotation knob where the source's knob
 *  lands under the reflection, so the two knobs mirror each other and the
 *  mirror's frame hugs its shape. A reflection across an axis at angle θ
 *  sends a knob at r to 2θ + 180° − r: −r for a vertical axis, 180° − r for
 *  a horizontal one. A point mirror is a half turn, so the knob lands at
 *  r + 180°.
 *
 *  Not folded into a half-turn range: the result follows the source
 *  continuously, so the knob a drag leaves behind is where the commit puts
 *  it rather than jumping to the opposite side past ±90°. */
export function mirroredRotation(sourceRot: number, m: MirrorMatrix): number {
  const det = m.m11 * m.m22 - m.m12 * m.m21;
  if (det >= 0) return normalizeRotation(sourceRot + 180);
  const twoTheta = (Math.atan2(m.m21, m.m11) * 180) / Math.PI;
  const d = normalizeRotation(twoTheta + 180 - sourceRot);
  return Math.abs(d) < 1e-9 ? 0 : d;
}

/** Strip derived-primitive metadata before re-computing it from the
 *  source (matches the offset deriver's contract). */
function stripDerivedPrimitiveFields(line: ConstructionLine): ConstructionLine {
  if (
    line.primitive === undefined &&
    line.kind === undefined &&
    line.cornerRounding === undefined &&
    line.rotation === undefined &&
    line.rotationCenter === undefined &&
    line.pivotPoint === undefined
  ) {
    return line;
  }
  const {
    primitive: _prim,
    kind: _kind,
    cornerRounding: _round,
    rotation: _rot,
    rotationCenter: _center,
    pivotPoint: _pivot,
    ...rest
  } = line;
  return rest;
}

/** Mirror the source's primitive metadata so the derived line can
 *  carry the right primitive after a reflection. Strategy:
 *   - `rectangle` / `ellipse`: the axis-aligned bbox is preserved
 *     by a `point` mirror (centre flips, dimensions stay). For a
 *     `segment` mirror it generally tilts; we leave the primitive
 *     in place but rotate the per-line `rotation` so the bbox
 *     overlay still tilts correctly. The user can detach the
 *     primitive by editing geometry later.
 *   - `line` / `pill`: explicit endpoints rotate cleanly under
 *     either reflection — reuse the existing `rotatePrimitive`
 *     helper composed with translate where needed. */
function mirrorPrimitive(
  prim: NonNullable<ConstructionLine["primitive"]>,
  m: MirrorMatrix,
): NonNullable<ConstructionLine["primitive"]> | null {
  if (prim.kind === "line" || prim.kind === "pill") {
    // Endpoint-based primitives mirror by mirroring each endpoint.
    const p1 = applyMirror(m, { x: prim.x1, y: prim.y1 });
    const p2 = applyMirror(m, { x: prim.x2, y: prim.y2 });
    return { ...prim, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
  }
  // rectangle / ellipse: only the centre / corner moves under a
  // point mirror (or an axis-aligned segment mirror). For a general
  // tilted axis, the resulting shape isn't axis-aligned and there's
  // no field to encode that on the primitive — drop the primitive
  // and let the derived line render as a generic polygon.
  // Heuristic: if the mirror is axis-aligned (matrix has zero
  // off-diagonals), the primitive's bbox transforms cleanly.
  const offDiag = Math.abs(m.m12) + Math.abs(m.m21);
  if (offDiag < 1e-6) {
    const corners = [
      applyMirror(m, { x: prim.x, y: prim.y }),
      applyMirror(m, { x: prim.x + prim.width, y: prim.y + prim.height }),
    ];
    const x = Math.min(corners[0]!.x, corners[1]!.x);
    const y = Math.min(corners[0]!.y, corners[1]!.y);
    return { ...prim, x, y, width: prim.width, height: prim.height };
  }
  return null;
}

// `translatePrimitive` and `rotatePrimitive` import side-effect kept
// so the bundler doesn't tree-shake them out — they're load-bearing
// for the rotated-mirror branch above when we want to compose moves.
void translatePrimitive;
void rotatePrimitive;

/** Bounding-box centre of a pathData string, or null when it won't parse.
 *  The self-axis gap needs a reference point that the reflection actually
 *  MOVES, so it can read the "away from the axis" direction off the
 *  displacement rather than deriving a normal and getting its sign wrong. */
function pathBoundsCenter(
  pathData: string,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  if (!pathData) return null;
  try {
    const cp = new scope.CompoundPath({ pathData, insert: false });
    const c = { x: cp.bounds.center.x, y: cp.bounds.center.y };
    cp.remove();
    return c;
  } catch {
    return null;
  }
}

/**
 * The reference centre a self-axis gap is measured from (#343).
 *
 * NOT the mirrored line's own source: a source and its offset descendants
 * mirror as ONE thing, so they must all be pushed by the SAME vector or the
 * mirrored group comes apart. `selfAxisGap` reads its direction off the
 * displacement the reflection produces, which differs per source the moment two
 * siblings aren't concentric — a single-EDGE offset is an arc fragment sitting
 * off to one side, so it reflects diagonally where the whole circle reflects
 * straight across, and its copy drifts away from its partner.
 *
 * Resolving to the offset ROOT gives every member of the chain one direction,
 * so the group translates rigidly. Falls back to the line itself when it isn't
 * an offset of anything.
 */
function gapReferenceCenter(
  project: Project,
  sourceId: string,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  const lines = projectConstructionLines(project);
  const rootId = resolveOffsetRoot(project, sourceId);
  const root =
    lines.find((l) => l.id === rootId) ?? lines.find((l) => l.id === sourceId);
  return root ? pathBoundsCenter(root.pathData, scope) : null;
}

/** Recompute every mirror line's `pathData` from its source + axis
 *  in the current project. Mirrors the contract of
 *  `deriveConstructionOffsets`: returns the input project unchanged
 *  when nothing needed updating, drops lines whose source OR axis
 *  has vanished. Called from the commit pipeline so derived
 *  geometry tracks every commit. */
export function deriveConstructionMirrors(
  project: Project,
  scope: paper.PaperScope,
): Project {
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return project;
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  let changed = false;
  const next: ConstructionLine[] = [];
  for (const line of lines) {
    const link = line.mirrorSource;
    if (!link) {
      next.push(line);
      continue;
    }
    const source = byId.get(link.sourceId);
    if (!source) {
      // Source vanished — drop the orphan.
      changed = true;
      continue;
    }
    const axisSpec = resolveAxisSpec(project, link.axis, scope);
    if (!axisSpec) {
      // Axis line / anchor vanished — same orphan rule.
      changed = true;
      continue;
    }
    // Also drop when the user has pointed the mirror at one of the
    // source's own anchors / segments AND the source has zero
    // length. Without this guard, a near-degenerate source produces
    // a zero-extent derived path that's invisible but still occupies
    // a layers-panel row.
    let mirror = buildMirrorMatrix(axisSpec);
    // Self-axis gap (#343): a copy mirrored across its SOURCE'S OWN edge lands
    // flush on top of it, so the gesture pushes it clear by the cursor's
    // distance from the axis. Folded into the reflection's translation rather
    // than applied to the derived path afterwards — same result, one transform,
    // and the primitive below is then mirrored through the same matrix the
    // geometry was.
    if (link.gapMm && link.gapMm > 0) {
      const centre = gapReferenceCenter(project, link.sourceId, scope);
      if (centre) {
        const g = selfAxisGap(axisSpec, centre, link.gapMm);
        mirror = { ...mirror, tx: mirror.tx + g.dx, ty: mirror.ty + g.dy };
      }
    }
    const derivedPath = mirrorPathData(source.pathData, mirror, scope);
    if (derivedPath === null) {
      next.push(line);
      continue;
    }
    const stripped = stripDerivedPrimitiveFields(line);
    const updated: ConstructionLine = { ...stripped, pathData: derivedPath };
    if (source.primitive) {
      const prim = mirrorPrimitive(source.primitive, mirror);
      if (prim) updated.primitive = prim;
    }
    if (source.kind !== undefined) {
      updated.kind = source.kind;
    }
    // Propagate anchor smoothness / angle locks (issue #180 follow-
    // up). `paper.CompoundPath.transform()` preserves segment order
    // on the simple shapes the mirror deriver produces, so the
    // mirror's anchor at `(pathIndex, segmentIndex)` corresponds to
    // the source's same `(pathIndex, segmentIndex)` — the lock
    // entries map 1:1 without re-indexing. Without this propagation
    // the mirror's anchor markers always render in the "independent"
    // visual state regardless of the source's lock annotations.
    // Explicitly clear (not just leave the stripped value alone)
    // when the source has none, so a previously-propagated lock
    // can't leak after the source's lock is removed.
    if (source.smoothAnchors && source.smoothAnchors.length > 0) {
      updated.smoothAnchors = source.smoothAnchors.map((entry) => ({
        ...entry,
      }));
    } else if (updated.smoothAnchors !== undefined) {
      delete updated.smoothAnchors;
    }
    // Inherit rotation through the axis matrix (#1192), so the mirror's
    // selection frame hugs the reflected shape. Downstream consumers
    // (`getUnrotatedBounds` for the selection overlay,
    // `applySourceRotationToPath` in the offset deriver) read these fields.
    // The angle comes from the matrix, not just the source: an untilted
    // source across an angled axis still lands tilted, and a source
    // mirrored across its own side turns its axis with it.
    const sourceRot = source.rotation ?? 0;
    const rot = mirroredRotation(sourceRot, mirror);
    const center = source.rotationCenter
      ? applyMirror(mirror, source.rotationCenter)
      : pathBoundsCenter(derivedPath, scope);
    if (Math.abs(rot) > 1e-6 && center) {
      updated.rotation = rot;
      updated.rotationCenter = center;
    }
    // A rectangle / ellipse primitive survives only an axis-aligned mirror,
    // and `mirrorPrimitive` mirrors its natural (un-rotated) form, which pairs
    // with a rotation of −r for a reflection, r for a point mirror. Across a
    // horizontal axis, or through a point, the knob-mirroring rotation is half
    // a turn from that, so turn the natural form the same half turn about the
    // rotation center to keep primitive + rotation describing the path the
    // deriver produced.
    const prim = updated.primitive;
    if (
      prim &&
      (prim.kind === "rectangle" || prim.kind === "ellipse") &&
      center
    ) {
      const det = mirror.m11 * mirror.m22 - mirror.m12 * mirror.m21;
      const primRot = det < 0 ? -sourceRot : sourceRot;
      if (Math.abs(normalizeRotation(rot - primRot)) > 90) {
        updated.primitive = {
          ...prim,
          x: 2 * center.x - prim.x - prim.width,
          y: 2 * center.y - prim.y - prim.height,
        };
      }
    }
    // Reflect the source's user-positioned pivot (issue #420) so the
    // mirror's always-on pivot crosshair lands at the mirrored point.
    // Independent of rotation — a never-rotated source can carry a
    // custom pivot.
    if (source.pivotPoint) {
      updated.pivotPoint = applyMirror(mirror, source.pivotPoint);
    }
    // If nothing changed compared to the existing line, skip the
    // snapshot rewrite so callers can short-circuit re-renders.
    // smoothAnchors compared as JSON because it's a small array of
    // plain objects and Object.is wouldn't recognise structurally-
    // identical fresh copies (we always rebuild `updated.smoothAnchors`
    // when source has any, so reference equality never holds).
    const smoothAnchorsUnchanged =
      JSON.stringify(line.smoothAnchors ?? null) ===
      JSON.stringify(updated.smoothAnchors ?? null);
    if (
      line.pathData === updated.pathData &&
      line.primitive === updated.primitive &&
      line.kind === updated.kind &&
      line.rotation === updated.rotation &&
      (line.rotationCenter?.x ?? null) ===
        (updated.rotationCenter?.x ?? null) &&
      (line.rotationCenter?.y ?? null) ===
        (updated.rotationCenter?.y ?? null) &&
      (line.pivotPoint?.x ?? null) === (updated.pivotPoint?.x ?? null) &&
      (line.pivotPoint?.y ?? null) === (updated.pivotPoint?.y ?? null) &&
      smoothAnchorsUnchanged
    ) {
      next.push(line);
      continue;
    }
    byId.set(line.id, updated);
    next.push(updated);
    changed = true;
  }
  if (!changed) return project;
  return { ...project, constructionLines: next };
}

/** True when `line` is a derived mirror line (its geometry is owned
 *  by another line + an axis). Symmetric with `isOffsetDerived` —
 *  every edit handler that already refuses offset derivatives can
 *  add this to refuse mirror derivatives too. */
export function isMirrorDerived(line: ConstructionLine): boolean {
  return !!line.mirrorSource;
}

/** Issue #180 follow-up: enforce the "mirror sibling" invariant on
 *  every commit. The contract:
 *
 *    For every existing mirror line M with `mirrorSource = { sourceId:
 *    S, axis: X }`, every line L that's a "logical sibling" of S
 *    under this axis must ALSO have a mirror sibling
 *    {sourceId: L, axis: X}.
 *
 *  "Logical sibling" of S = any line whose presence is explained by
 *  a chain of offset / mirror back-refs that ultimately routes
 *  through S. Concretely:
 *
 *    1. Offset descendants of S: any line whose `offsetSource`
 *       chain ends at S. These are the "visual offsets" of S, and
 *       their mirrors across X are the "visual offsets" of M.
 *    2. If S is itself a mirror (sourceId=S', axis=X'), apply the
 *       rule recursively: every logical sibling of S' across X'
 *       needs a mirror across X too. This is what makes
 *       mirror-of-mirror carry the offset chain through both levels.
 *
 *  Missing siblings are auto-created. Each new mirror line gets a
 *  fresh ID, an auto-name, and seed `pathData` from the sibling's
 *  current pathData (which the next deriver pass will overwrite
 *  with the canonical mirrored geometry).
 *
 *  Why mirror-of-offset (not offset-of-mirror): mirror is a cheap
 *  closed-form matrix transform; paperjs-offset is O(samples ×
 *  segments). Auto-creating mirrors of the already-computed offset
 *  pathData costs N matrix transforms per N mirror levels; auto-
 *  creating offsets of mirrors would cost N paperjs-offset calls.
 *  Edit-redirect at the tool layer routes drags / rotations on
 *  mirror-derived lines back to the chain root, so the editing UX
 *  doesn't need to invert through offsets to follow the chain. */
export function enforceMirrorSiblingInvariant(
  project: Project,
  scope: paper.PaperScope,
  newId: () => string,
): Project {
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return project;

  // Group existing mirrors by (axis-key, source-id). Axis key
  // serializes the axis ref into a deterministic string so we can
  // use it as a Map key.
  const axisKey = (
    axis: NonNullable<ConstructionLine["mirrorSource"]>["axis"],
  ): string =>
    `${axis.kind}:${axis.constructionId}:${axis.pathIndex}:${axis.segmentIndex}`;

  // For each axis (X), set of source ids that already have a mirror
  // across X. Used to detect missing siblings.
  const haveMirrorByAxis = new Map<string, Set<string>>();
  // For each axis (X), the FULL axis ref (so we can construct new
  // mirror lines using the same ref shape).
  const axisByKey = new Map<
    string,
    NonNullable<ConstructionLine["mirrorSource"]>["axis"]
  >();
  // …and the self-axis GAP the existing mirrors across that axis were given
  // (#343). A sibling created here must inherit it, or the mirrored pair comes
  // apart: mirror the circle 60 mm clear of its own edge and its offset's
  // mirror, created without the gap, lands 60 mm short — no longer concentric
  // with the thing it offsets.
  const gapByKey = new Map<string, number>();
  for (const l of lines) {
    if (!l.mirrorSource) continue;
    const key = axisKey(l.mirrorSource.axis);
    if (!haveMirrorByAxis.has(key)) haveMirrorByAxis.set(key, new Set());
    haveMirrorByAxis.get(key)!.add(l.mirrorSource.sourceId);
    axisByKey.set(key, l.mirrorSource.axis);
    const g = l.mirrorSource.gapMm;
    if (g != null && g > 0) gapByKey.set(key, g);
  }

  // Pre-compute offset-children-by-source so the loop below stays
  // O(N) regardless of chain depth.
  const offsetChildrenBySource = new Map<string, string[]>();
  for (const l of lines) {
    const s = l.offsetSource?.sourceId;
    if (!s) continue;
    if (!offsetChildrenBySource.has(s)) offsetChildrenBySource.set(s, []);
    offsetChildrenBySource.get(s)!.push(l.id);
  }
  const offsetDescendants = (originId: string): Set<string> => {
    const out = new Set<string>();
    const queue: string[] = [originId];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      const kids = offsetChildrenBySource.get(cur);
      if (!kids) continue;
      for (const k of kids) {
        if (out.has(k)) continue;
        out.add(k);
        queue.push(k);
      }
    }
    return out;
  };

  // For each axis with at least one mirror, compute the full set
  // of "logical siblings" reachable from any existing mirror's
  // source. Then ensure each of those siblings has its own mirror
  // across the same axis; create one if missing.
  const newLines: ConstructionLine[] = [];
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  for (const [key, sourceIds] of haveMirrorByAxis) {
    const axis = axisByKey.get(key)!;
    // Cross-axis sibling lookup expands the BFS through mirror-of-
    // mirror clusters in a single pass. When walking sources for
    // axis X, if a source M1 is itself a mirror with axis X1, fold
    // in every X1-sibling's source id — so M2 = mirror(M1, X2)
    // naturally picks up the auto-created mirror-of-offset siblings
    // of M1 without needing a second outer iteration.
    const seen = new Set<string>(sourceIds);
    const queue: string[] = [...sourceIds];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      // Direct offset descendants of `cur`.
      for (const d of offsetDescendants(cur)) {
        if (!seen.has(d)) {
          seen.add(d);
          queue.push(d);
        }
      }
      // If `cur` is itself a mirror, look up its own siblings by
      // axis and source. The other mirrors with the same axis as
      // `cur` form a sibling group; their corresponding LINES are
      // the logical siblings of `cur` for OUR axis.
      const curLine = byId.get(cur);
      if (curLine?.mirrorSource) {
        const curAxisKey = axisKey(curLine.mirrorSource.axis);
        const siblingSources = haveMirrorByAxis.get(curAxisKey);
        if (siblingSources) {
          for (const sibSourceId of siblingSources) {
            if (sibSourceId === curLine.mirrorSource.sourceId) continue;
            const sibLine = lines.find(
              (l) =>
                l.mirrorSource?.sourceId === sibSourceId &&
                axisKey(l.mirrorSource.axis) === curAxisKey,
            );
            if (sibLine && !seen.has(sibLine.id)) {
              seen.add(sibLine.id);
              queue.push(sibLine.id);
            }
          }
        }
      }
    }
    // Remove the existing-mirror sources (they already have
    // mirrors across this axis) — we only create lines for the
    // missing ones.
    for (const existing of sourceIds) seen.delete(existing);
    for (const missingId of seen) {
      const sibSource = byId.get(missingId);
      if (!sibSource) continue;
      // Skip if creating this would form a degenerate self-mirror
      // (sibling source IS the axis line).
      if (axis.constructionId === missingId) continue;
      const axisSpec = resolveAxisSpec(project, axis, scope);
      if (!axisSpec) continue;
      let matrix = buildMirrorMatrix(axisSpec);
      const sibGap = gapByKey.get(axisKey(axis));
      if (sibGap != null && sibGap > 0) {
        const centre = gapReferenceCenter(project, missingId, scope);
        if (centre) {
          const g = selfAxisGap(axisSpec, centre, sibGap);
          matrix = { ...matrix, tx: matrix.tx + g.dx, ty: matrix.ty + g.dy };
        }
      }
      const seedPath = mirrorPathData(sibSource.pathData, matrix, scope);
      if (seedPath === null) continue;
      const baseName = sibSource.name
        ? `${sibSource.name} (mirrored)`
        : "Construction (mirrored)";
      newLines.push({
        id: newId(),
        name: baseName,
        pathData: seedPath,
        mirrorSource: {
          sourceId: missingId,
          axis,
          ...(sibGap != null && sibGap > 0 ? { gapMm: sibGap } : {}),
        },
      });
    }
  }

  if (newLines.length === 0) return project;
  return {
    ...project,
    constructionLines: [...lines, ...newLines],
  };
}

/** Prune mirror-source references that would leave a mirrored line
 *  pointing at a line id in `deletedIds`. Used by the layers panel /
 *  delete handlers alongside `pruneConstraintsForDeletedLines`. */
export function pruneMirrorsForDeletedLines(
  project: Project,
  deletedIds: ReadonlySet<string>,
): Project {
  if (deletedIds.size === 0) return project;
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return project;
  let changed = false;
  const next = lines.filter((l) => {
    const link = l.mirrorSource;
    if (!link) return true;
    if (
      deletedIds.has(link.sourceId) ||
      deletedIds.has(link.axis.constructionId)
    ) {
      changed = true;
      return false;
    }
    return true;
  });
  return changed ? { ...project, constructionLines: next } : project;
}
